import React, { useState } from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import FileUploader from 'devextreme-react/file-uploader';
import * as Store from '@apply/store';
import PdfIFrameViewer from '../viewers/PdfIFrameViewer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { Button, ButtonTheme } from '@apply/components';
import { isMobileScreen } from '../helpers';

export interface FloodReportEsignModalProps {
    isOpen: boolean;
    onCloseCallback?: (tempfileName: string) => void;
    notice?: Store.UserStore.Types.UserNotice;
}

export const FloodReportEsignModal: React.FC<FloodReportEsignModalProps> = ({ isOpen, onCloseCallback, notice }) => {
    const mobileScreen = isMobileScreen();
    const [fileToUpload, set_fileToUpload] = useState(null);

    const maxFileSize = 1024 * 1024 * 35;
    const [canContinue, set_canContinue] = useState(false);

    const handleClose = () => {
        onCloseCallback(null);
    };

    const styles: Record<string, React.CSSProperties> = {
        flexboxContainer: {
            display: 'flex',
            justifyContent: 'center',
        },
        signatureContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: 'center',
            paddingRight: '0 !important', // Override custom.css
        },
        buttonDiv: {
            paddingTop: 20,
            paddingRight: 10,
            paddingBottom: 0,
            paddingLeft: 10,
        },
        signButton: {
            margin: 0,
        },
        modalActions: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            gap: '50px',
            paddingTop: 20,
            alignItems: 'center',
        },
        list: {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px', // space between list items
            paddingLeft: '20px', // keeps normal list indentation
        },
    };

    const onFilesChanged = (fileArray) => {
        const files = fileArray.filter((file) => file.size <= maxFileSize);
        if (files && files.length > 0) {
            set_canContinue(true);
        } else {
            set_canContinue(false);
        }
        set_fileToUpload(files[0]);
    };

    const submitFile = () => {
        onCloseCallback(fileToUpload);
    };

    if (!notice) {
        return null;
    }

    return (
        <div>
            <Dialog
                open={isOpen}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
                maxWidth='md'
                fullWidth={true}
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            role='dialog'
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>{notice.signedFile ? 'Signed Flood Notice' : 'Sign Flood Notice'}</h1>
                                </div>
                                {notice.eSignDate && notice.signedFile && notice.signedFile.url && (
                                    <PdfIFrameViewer
                                        title='noticeIFrame'
                                        url={notice.signedFile.url}
                                        height='700px'
                                        width='100%'
                                        viewerOptions={{ fitWidth: true }}
                                    />
                                )}
                                <div className='modal-content modal-content--esign'>
                                    <div className='modal-body'>
                                        <div
                                            className='standard-form'
                                            style={styles.noPadding}
                                        >
                                            {!(notice.eSignDate && notice.signedFile) && (
                                                <ol style={styles.list}>
                                                    <li>
                                                        Please click{' '}
                                                        <a
                                                            href={notice.noticeFile.url}
                                                            target='_blank'
                                                            rel='noreferrer'
                                                        >
                                                            here
                                                        </a>{' '}
                                                        to download the flood report, or click on the download icon{' '}
                                                        <a
                                                            href={notice.noticeFile.url}
                                                            target='_blank'
                                                            rel='noreferrer'
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faDownload}
                                                                aria-hidden='true'
                                                            />
                                                        </a>
                                                    </li>
                                                    <li>
                                                        After download please print the document, sign and the scan. OR use a tool
                                                        such as Adobe reader to sign the document electronically.
                                                    </li>
                                                    <li>
                                                        Click on the 'Select File' button below and select the signed flood
                                                        report.
                                                    </li>
                                                    <div>
                                                        <div
                                                            id='drop_zone'
                                                            style={{
                                                                width: '100%',
                                                                backgroundColor: 'silver',
                                                                padding: '10px',
                                                            }}
                                                        >
                                                            <FileUploader
                                                                selectButtonText='Select File'
                                                                uploadMode='useForm'
                                                                maxFileSize={maxFileSize}
                                                                invalidFileExtensionMessage='Invalid File Type. File will be ignored'
                                                                invalidMaxFileSizeMessage='File too large. File will be ignored'
                                                                multiple={false}
                                                                allowedFileExtensions={['.pdf', '.png', '.jpg']}
                                                                allowCanceling={true}
                                                                labelText={mobileScreen ? '' : 'or Drag & Drop the file here'}
                                                                onValueChange={onFilesChanged}
                                                            />
                                                        </div>
                                                    </div>
                                                    <li>Click on the Submit button to upload the signed flood report.</li>
                                                </ol>
                                            )}
                                            <div
                                                className={
                                                    mobileScreen ? 'mobile-modal-actions-container' : 'modal-content-actions'
                                                }
                                                style={!mobileScreen ? styles.modalActions : undefined}
                                            >
                                                {!(notice.eSignDate && notice.signedFile) && (
                                                    <Button
                                                        id='accept-button'
                                                        key='accept-button'
                                                        data-testid='accept-button'
                                                        theme={ButtonTheme.Primary}
                                                        handleClick={submitFile}
                                                        disabled={!canContinue}
                                                        width={mobileScreen ? '100%' : '150px'}
                                                        height={mobileScreen ? '38px' : '48px'}
                                                    >
                                                        Submit
                                                    </Button>
                                                )}
                                                <Button
                                                    id='do-not-accept-button'
                                                    key='do-not-accept-button'
                                                    data-testid='do-not-accept-button'
                                                    theme={ButtonTheme.Tertiary}
                                                    handleClick={handleClose}
                                                    width={mobileScreen ? '100%' : '150px'}
                                                    height={mobileScreen ? '38px' : '48px'}
                                                >
                                                    Close
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions></DialogActions>
            </Dialog>
        </div>
    );
};
